import { z } from 'zod';
import { kePhoneSchema } from './common.schemas';
import { defineListQuery, listFilter } from './list-query.schemas';

export const EMPLOYMENT_STATUSES = ['active', 'suspended', 'left'] as const;
export type EmploymentStatus = (typeof EMPLOYMENT_STATUSES)[number];

/** Worked out from events and employment status, never typed (gitaumoses4/chukua#54); the order is the list filter's. */
export const DRIVER_AVAILABILITY_STATUSES = ['available', 'on_trip', 'resting', 'off_duty', 'suspended'] as const;
export type DriverAvailabilityStatus = (typeof DRIVER_AVAILABILITY_STATUSES)[number];

/**
 * `POST /drivers` and `PATCH /drivers/:id` — employment status changes go through their own `POST /drivers/:id/status`.
 * Strict, as is the status change below, so a body carrying `availability` is refused rather than silently dropped:
 * it is derived, never set (gitaumoses4/chukua#54).
 */
export const createDriverSchema = z
  .object({
    name: z.string().trim().min(1).max(160),
    phone: kePhoneSchema,
    nationalId: z.string().trim().min(1).max(40),
    dateOfBirth: z.iso.date(),
    licenceNumber: z.string().trim().min(1).max(40),
    licenceClasses: z.array(z.string().trim().min(1).max(10)).min(1),
    hazmatCertified: z.boolean(),
  })
  .strict();
export type CreateDriverInput = z.infer<typeof createDriverSchema>;

export const updateDriverSchema = createDriverSchema;
export type UpdateDriverInput = z.infer<typeof updateDriverSchema>;

/** `POST /drivers/:id/status` — one endpoint for every transition; nothing restricts which are legal. */
export const driverStatusChangeSchema = z
  .object({
    status: z.enum(EMPLOYMENT_STATUSES),
    effectiveAt: z.iso.date(),
    reason: z.string().trim().min(1).max(500).optional(),
  })
  .strict();
export type DriverStatusChangeInput = z.infer<typeof driverStatusChangeSchema>;

/** `GET /drivers` — search matches name, phone and licence number, not `nationalId` (CLAUDE.md → Data protection). */
export const driverListQuerySchema = defineListQuery({
  sortFields: ['name', 'createdAt', 'availability'],
  defaultSort: { sort: 'name', order: 'asc' },
  filters: {
    status: listFilter(z.enum(EMPLOYMENT_STATUSES)),
    availability: listFilter(z.enum(DRIVER_AVAILABILITY_STATUSES)),
  },
});
export type DriverListQuery = z.infer<typeof driverListQuerySchema>;

/** `GET /drivers/:id/availability-history` — newest first. */
export const driverAvailabilityHistoryQuerySchema = defineListQuery({
  sortFields: ['occurredAt'],
  defaultSort: { sort: 'occurredAt', order: 'desc' },
  filters: {},
});
export type DriverAvailabilityHistoryQuery = z.infer<typeof driverAvailabilityHistoryQuerySchema>;

/** One row of the append-only history behind a driver's availability. */
export interface DriverAvailabilityHistoryEntry {
  id: string;
  fromStatus: DriverAvailabilityStatus | null;
  toStatus: DriverAvailabilityStatus;
  reason: string;
  occurredAt: string;
}

/** What the detail screen shows, and what a future eligibility policy reads. */
export interface DriverAvailability {
  status: DriverAvailabilityStatus;
  since: string;
  lastTripEndAt: string | null;
}
